import { Box, Container, Typography, Button, Grid } from "@mui/material";
import { Link } from "react-router-dom";
import { useDocumentTitle } from "../../utils/data";
import { keyframes } from "@mui/system";

const floatBall = keyframes`
  0%, 100% { transform: translate(0, 0); }
  25% { transform: translate(20px, -20px); }
  50% { transform: translate(-10px, 20px); }
  75% { transform: translate(10px, -10px); }
`;

const scaleInCenter = keyframes`
  from { opacity: 0; transform: scale(0.85); }
  to { opacity: 1; transform: scale(1); }
`;

const slideInLeft = keyframes`
  from {
    opacity: 0;
    transform: translateX(-100px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
`;

const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(50px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const moveCircle1 = keyframes`
  0%, 100% {
    transform: translate(0, 0);
  }
  25% {
    transform: translate(30px, -40px);
  }
  50% {
    transform: translate(-20px, -30px);
  }
  75% {
    transform: translate(40px, 20px);
  }
`;

const moveCircle2 = keyframes`
  0%, 100% {
    transform: translate(0, 0);
  }
  25% {
    transform: translate(-40px, 20px);
  }
  50% {
    transform: translate(30px, 40px);
  }
  75% {
    transform: translate(-30px, -40px);
  }
`;

const moveCircle3 = keyframes`
  0%, 100% {
    transform: translate(0, 0);
  }
  25% {
    transform: translate(20px, 50px);
  }
  50% {
    transform: translate(-40px, 30px);
  }
  75% {
    transform: translate(40px, -20px);
  }
`;

const serviceOfferings = [
  {
    icon: "🔄",
    title: "Transformation & Discovery",
    desc: "We help identify business needs and create a strategic plan for technology transformation."
  },
  {
    icon: "🏗️",
    title: "Discovery & Architect",
    desc: "Our experts design scalable and efficient solutions tailored to your unique business requirements."
  },
  {
    icon: "✅",
    title: "QA & Testing",
    desc: "We ensure high-quality applications by rigorously testing and optimizing for performance and functionality."
  },
  {
    icon: "🚀",
    title: "End to End Development",
    desc: "We provide comprehensive development services, from concept to deployment, ensuring seamless delivery."
  },
  {
    icon: "⬆️",
    title: "Upgrades & Migrations",
    desc: "We manage smooth upgrades and migrations, ensuring your applications stay current and efficient."
  },
  {
    icon: "🛠️",
    title: "Support & Enhancements",
    desc: "Our ongoing support and enhancements keep your applications optimized and adaptable to future needs."
  }
];

const whyChooseUs = [
  {
    icon: "📊",
    title: "20+ Years' Experience",
    desc: "Expertise in design consulting, technical advisory, and web development, providing innovative solutions for success."
  },
  {
    icon: "👨‍💼",
    title: "Highly Skilled Team",
    desc: "A proficient team of core web technology and UX/UI experts, backed by a dedicated Quality Assurance team."
  },
  {
    icon: "🎯",
    title: "High-Quality Deliverables",
    desc: "Our expert team works collaboratively to deliver exceptional results that surpass client expectations."
  },
  {
    icon: "🤝",
    title: "Full-Service Partner",
    desc: "Your trusted partner for application development, support, and innovative enhancements, ensuring long-term success."
  }
];

const CustomAppDevelopment = () => {
  useDocumentTitle("Briozon - Custom Application Development");

  return (
    <Box>
      {/* Hero Banner */}
      <Box
        sx={{
          background: "linear-gradient(135deg, #f8fafc 0%, #f0f4ff 100%)",
          py: { xs: 6, sm: 8, md: 10 },
          position: "relative",
          overflow: "hidden",
        }}
      >
        {/* Animated background circles */}
        <Box
          sx={{
            position: "absolute",
            top: "5%",
            right: "10%",
            width: 150,
            height: 150,
            borderRadius: "50%",
            background: "radial-gradient(circle, rgba(91, 124, 250, 0.15) 0%, rgba(91, 124, 250, 0) 70%)",
            animation: `${moveCircle1} 15s ease-in-out infinite`,
            pointerEvents: "none",
          }}
        />
        <Box
          sx={{
            position: "absolute",
            bottom: "10%",
            left: "5%",
            width: 200,
            height: 200,
            borderRadius: "50%",
            background: "radial-gradient(circle, rgba(118, 75, 162, 0.12) 0%, rgba(118, 75, 162, 0) 70%)",
            animation: `${moveCircle2} 18s ease-in-out infinite`,
            pointerEvents: "none",
          }}
        />
        <Box
          sx={{
            position: "absolute",
            top: "40%",
            right: "5%",
            width: 120,
            height: 120,
            borderRadius: "50%",
            background: "radial-gradient(circle, rgba(91, 124, 250, 0.1) 0%, rgba(91, 124, 250, 0) 70%)",
            animation: `${moveCircle3} 20s ease-in-out infinite`,
            pointerEvents: "none",
          }}
        />

        {/* Floating decorative elements */}
        <Box
          sx={{
            position: "absolute",
            top: "10%",
            right: "5%",
            width: 120,
            height: 120,
            borderRadius: "50%",
            background: "linear-gradient(135deg, rgba(91,124,250,0.1) 0%, rgba(118,75,162,0.1) 100%)",
            animation: `${floatBall} 8s ease-in-out infinite`,
          }}
        />
        <Box
          sx={{
            position: "absolute",
            bottom: "15%",
            left: "3%",
            width: 80,
            height: 80,
            borderRadius: "50%",
            background: "linear-gradient(135deg, rgba(118,75,162,0.1) 0%, rgba(91,124,250,0.1) 100%)",
            animation: `${floatBall} 6s ease-in-out infinite 1s`,
          }}
        />

        <Container maxWidth="lg">
          <Grid container spacing={{ xs: 4, sm: 6, md: 8 }} alignItems="center">
            {/* Left Content */}
            <Grid item xs={12} md={6}>
              <Box>
                <Typography
                  variant="overline"
                  sx={{
                    fontSize: { xs: "12px", sm: "14px" },
                    fontWeight: 700,
                    color: "#5b7cfa",
                    letterSpacing: "2px",
                    mb: 1,
                    display: "block",
                  }}
                >
                  APPLICATION DEVELOPMENT SERVICES
                </Typography>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { xs: "32px", sm: "42px", md: "48px" },
                    fontWeight: 800,
                    color: "#1b1f33",
                    lineHeight: 1.2,
                    mb: 2,
                  }}
                >
                  Custom{" "}
                  <Box
                    component="span"
                    sx={{
                      background: "linear-gradient(135deg, #5b7cfa 0%, #764ba2 100%)",
                      WebkitBackgroundClip: "text",
                      WebkitTextFillColor: "transparent",
                      backgroundClip: "text",
                    }}
                  >
                    Application Development
                  </Box>
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    fontSize: { xs: "16px", sm: "19px", md: "20px" },
                    color: "#4c5472",
                    lineHeight: 1.8,
                    mb: 3,
                    textAlign: "justify",
                  }}
                >
                  We specialize in custom software development, delivering innovative solutions tailored to your business needs. Our experienced team is dedicated to turning your ideas into impactful technology that drives transformation and growth.
                </Typography>
                <Button
                  component={Link}
                  to="/contact"
                  variant="contained"
                  sx={{
                    background: "linear-gradient(135deg, #5b7cfa 0%, #764ba2 100%)",
                    color: "#fff",
                    fontWeight: 700,
                    px: 4,
                    py: 1.5,
                    borderRadius: "12px",
                    fontSize: "16px",
                    textTransform: "none",
                    boxShadow: "0 8px 24px rgba(91, 124, 250, 0.3)",
                    transition: "all 0.3s ease",
                    "&:hover": {
                      transform: "translateY(-4px)",
                      boxShadow: "0 12px 32px rgba(91, 124, 250, 0.4)",
                    },
                  }}
                >
                  Get Started
                </Button>
              </Box>
            </Grid>

            {/* Right - Creative Visual */}
            <Grid item xs={12} md={6}>
              <Box
                sx={{
                  position: "relative",
                  height: { xs: 320, sm: 400, md: 450 },
                  animation: `${scaleInCenter} 0.8s ease-out 0.2s both`,
                }}
              >
                {/* Main gradient circle */}
                <Box
                  sx={{
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    transform: "translate(-50%, -50%)",
                    width: { xs: 220, sm: 280, md: 320 },
                    height: { xs: 220, sm: 280, md: 320 },
                    borderRadius: "50%",
                    background: "linear-gradient(135deg, rgba(91,124,250,0.15) 0%, rgba(118,75,162,0.15) 100%)",
                    filter: "blur(40px)",
                  }}
                />

                {/* Central icon */}
                <Box
                  sx={{
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    transform: "translate(-50%, -50%)",
                    width: { xs: 100, sm: 120 },
                    height: { xs: 100, sm: 120 },
                    borderRadius: "24px",
                    background: "linear-gradient(135deg, #5b7cfa 0%, #764ba2 100%)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: { xs: "48px", sm: "56px" },
                    boxShadow: "0 20px 40px rgba(91, 124, 250, 0.4)",
                    animation: `${floatBall} 6s ease-in-out infinite`,
                    zIndex: 2,
                  }}
                >
                  💻
                </Box>

                {/* Floating feature cards */}
                <Box
                  sx={{
                    position: "absolute",
                    top: "8%",
                    left: "10%",
                    background: "#fff",
                    borderRadius: "16px",
                    px: 2.5,
                    py: 1.5,
                    boxShadow: "0 8px 24px rgba(0,0,0,0.1)",
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                    animation: `${floatBall} 7s ease-in-out infinite 0.5s`,
                    zIndex: 1,
                  }}
                >
                  <Box sx={{ fontSize: "24px" }}>React</Box>
                  <Typography sx={{ fontWeight: 600, color: "#1b1f33", fontSize: "13px" }}>
                    React
                  </Typography>
                </Box>

                <Box
                  sx={{
                    position: "absolute",
                    top: "15%",
                    right: "5%",
                    background: "#fff",
                    borderRadius: "16px",
                    px: 2.5,
                    py: 1.5,
                    boxShadow: "0 8px 24px rgba(0,0,0,0.1)",
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                    animation: `${floatBall} 8s ease-in-out infinite 1s`,
                    zIndex: 1,
                  }}
                >
                  <Box sx={{ fontSize: "24px" }}>Node</Box>
                  <Typography sx={{ fontWeight: 600, color: "#1b1f33", fontSize: "13px" }}>
                    Node.js
                  </Typography>
                </Box>

                <Box
                  sx={{
                    position: "absolute",
                    bottom: "20%",
                    left: "5%",
                    background: "#fff",
                    borderRadius: "16px",
                    px: 2.5,
                    py: 1.5,
                    boxShadow: "0 8px 24px rgba(0,0,0,0.1)",
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                    animation: `${floatBall} 9s ease-in-out infinite 1.5s`,
                    zIndex: 1,
                  }}
                >
                  <Box sx={{ fontSize: "24px" }}>.Net</Box>
                  <Typography sx={{ fontWeight: 600, color: "#1b1f33", fontSize: "13px" }}>
                    .NET
                  </Typography>
                </Box>

                <Box
                  sx={{
                    position: "absolute",
                    bottom: "10%",
                    right: "10%",
                    background: "#fff",
                    borderRadius: "16px",
                    px: 2.5,
                    py: 1.5,
                    boxShadow: "0 8px 24px rgba(0,0,0,0.1)",
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                    animation: `${floatBall} 6s ease-in-out infinite 2s`,
                    zIndex: 1,
                  }}
                >
                  <Box sx={{ fontSize: "24px" }}>Angular</Box>
                  <Typography sx={{ fontWeight: 600, color: "#1b1f33", fontSize: "13px" }}>
                    Angular
                  </Typography>
                </Box>

                {/* Decorative rings */}
                <Box
                  sx={{
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    transform: "translate(-50%, -50%)",
                    width: { xs: 200, sm: 260 },
                    height: { xs: 200, sm: 260 },
                    borderRadius: "50%",
                    border: "2px dashed rgba(91, 124, 250, 0.2)",
                    animation: `${floatBall} 10s ease-in-out infinite`,
                  }}
                />
                <Box
                  sx={{
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    transform: "translate(-50%, -50%)",
                    width: { xs: 280, sm: 360 },
                    height: { xs: 280, sm: 360 },
                    borderRadius: "50%",
                    border: "1px dashed rgba(118, 75, 162, 0.15)",
                    animation: `${floatBall} 12s ease-in-out infinite reverse`,
                  }}
                />
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Section 2: Creating Tailored Cross-Platform Applications */}
      <Box sx={{ py: { xs: 8, md: 10 }, backgroundColor: "#fff" }}>
        <Container maxWidth="lg">
          <Grid container spacing={{ xs: 4, md: 8 }} alignItems="flex-start">
            <Grid item xs={12} md={6}>
              <Box
                sx={{
                  width: 60,
                  height: 4,
                  background: "linear-gradient(90deg, #5b7cfa 0%, #764ba2 100%)",
                  borderRadius: "2px",
                  mb: 3,
                  animation: `${slideInLeft} 0.8s ease-out`,
                }}
              />
              <Typography
                variant="h3"
                sx={{
                  fontWeight: 700,
                  fontSize: { xs: "28px", sm: "36px", md: "42px" },
                  color: "#1b1f33",
                  mb: 3,
                  animation: `${slideInLeft} 0.8s ease-out 0.1s both`,
                }}
              >
                Creating{" "}
                <Box
                  component="span"
                  sx={{
                    background: "linear-gradient(135deg, #5b7cfa 0%, #764ba2 100%)",
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                    backgroundClip: "text",
                  }}
                >
                  Tailored Cross-Platform Applications
                </Box>
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  color: "#4c5472",
                  lineHeight: 1.8,
                  fontSize: { xs: "16px", sm: "19px", md: "20px" },
                  mb: 3,
                  textAlign: "justify",
                  animation: `${fadeInUp} 0.8s ease-out 0.2s both`,
                }}
              >
                Responsive web design enables seamless adaptation across devices, but some applications require platform-specific features like gestures and native interfaces. Briozon develops custom cross-platform solutions that optimize performance and user experience on modern devices.
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box
                sx={{
                  p: { xs: 3, md: 4 },
                  borderRadius: "20px",
                  background: "linear-gradient(135deg, #f0f4ff 0%, #f7f0ff 100%)",
                  animation: `${fadeInUp} 0.8s ease-out 0.2s both`,
                }}
              >
                <Typography
                  variant="h6"
                  sx={{ fontWeight: 700, color: "#1b1f33", mb: 3, fontSize: { xs: "18px", sm: "20px" } }}
                >
                  Our Technology Stack
                </Typography>
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
                  {["React", "Angular", "Node.js", "PHP", ".NET", "Java", "TypeScript", "REST APIs"].map((tech) => (
                    <Box
                      key={tech}
                      sx={{
                        px: 3,
                        py: 1.2,
                        borderRadius: "30px",
                        background: "#fff",
                        color: "#5b7cfa",
                        fontWeight: 600,
                        fontSize: { xs: "14px", sm: "15px" },
                        border: "1.5px solid rgba(91, 124, 250, 0.3)",
                        boxShadow: "0 2px 8px rgba(91, 124, 250, 0.08)",
                        transition: "all 0.3s ease",
                        "&:hover": {
                          background: "linear-gradient(135deg, #5b7cfa 0%, #764ba2 100%)",
                          color: "#fff",
                          borderColor: "transparent",
                          transform: "translateY(-2px)",
                          boxShadow: "0 6px 16px rgba(91, 124, 250, 0.25)",
                        },
                      }}
                    >
                      {tech}
                    </Box>
                  ))}
                </Box>
              </Box>
            </Grid>
          </Grid>

          {/* Full-width highlight strip */}
          <Box
            sx={{
              mt: { xs: 5, md: 6 },
              p: { xs: 3, md: "32px 40px" },
              borderRadius: "20px",
              background: "linear-gradient(135deg, #f0f4ff 0%, #f7f0ff 100%)",
              border: "1px solid rgba(91,124,250,0.12)",
              display: "flex",
              alignItems: "flex-start",
              gap: { xs: 2.5, md: 3.5 },
              animation: `${fadeInUp} 0.8s ease-out 0.4s both`,
              position: "relative",
              overflow: "hidden",
            }}
          >
            {/* Left accent bar */}
            <Box
              sx={{
                flexShrink: 0,
                width: 5,
                alignSelf: "stretch",
                borderRadius: "4px",
                background: "linear-gradient(180deg, #5b7cfa 0%, #764ba2 100%)",
              }}
            />
            <Typography
              variant="body1"
              sx={{
                flex: 1,
                color: "#4c5472",
                lineHeight: 1.9,
                fontSize: { xs: "16px", sm: "18px", md: "19px" },
                fontStyle: "italic",
              }}
            >
              Briozon's offshore teams of full-stack developers specialize in the latest web technologies, including Angular, React, Node, PHP, and .NET. We assist in building and enhancing web applications across domains, supporting multiple devices and touchpoints through responsive design principles and seamless system integrations. As a trusted partner, we walk alongside you on your Digital Transformation journey, ensuring the success of your initiatives.
            </Typography>
          </Box>

        </Container>
      </Box>

      {/* Section 3: Expertise - Alternating Layout */}
      <Box sx={{ py: { xs: 8, md: 10 }, backgroundColor: "#fafbff" }}>
        <Container maxWidth="lg">
          {/* Row 1: API Integration */}
          <Grid container spacing={{ xs: 4, md: 8 }} alignItems="center" sx={{ mb: { xs: 8, md: 10 } }}>
            <Grid item xs={12} md={6}>
              <Typography
                variant="h3"
                sx={{
                  fontWeight: 700,
                  fontSize: { xs: "28px", sm: "36px", md: "42px" },
                  color: "#1b1f33",
                  mb: 3,
                }}
              >
                Expertise in{" "}
                <Box
                  component="span"
                  sx={{
                    background: "linear-gradient(135deg, #5b7cfa 0%, #764ba2 100%)",
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                  }}
                >
                  API Integration
                </Box>
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  color: "#4c5472",
                  lineHeight: 1.8,
                  fontSize: { xs: "16px", sm: "19px", md: "20px" },
                  textAlign: "justify",
                }}
              >
                Expertise in API integration involves understanding the technical nuances of connecting disparate systems and translating data between them. We have technical experts with deep understanding of API protocols, data formats, and authentication methods to create seamless and efficient integrations.
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box
                sx={{
                  p: { xs: 3, md: 4 },
                  borderRadius: "20px",
                  background: "linear-gradient(135deg, rgba(91,124,250,0.06) 0%, rgba(118,75,162,0.06) 100%)",
                  border: "1px solid rgba(91,124,250,0.12)",
                  display: "flex",
                  flexDirection: "column",
                  gap: 2.5,
                }}
              >
                {[
                  { icon: "🔗", label: "API Protocols & Standards" },
                  { icon: "🔐", label: "Authentication & Security" },
                  { icon: "⚡", label: "Real-Time Data Integration" },
                ].map((item) => (
                  <Box key={item.label} sx={{ display: "flex", alignItems: "center", gap: 2.5 }}>
                    <Box
                      sx={{
                        width: 44,
                        height: 44,
                        borderRadius: "12px",
                        background: "linear-gradient(135deg, rgba(91,124,250,0.12), rgba(118,75,162,0.12))",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: "1.3rem",
                        flexShrink: 0,
                      }}
                    >
                      {item.icon}
                    </Box>
                    <Typography sx={{ fontWeight: 600, color: "#1b1f33", fontSize: { xs: "16px", sm: "18px" } }}>
                      {item.label}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Grid>
          </Grid>

          {/* Row 2: Innovating Seamless Application Experiences */}
          <Grid container spacing={{ xs: 4, md: 8 }} alignItems="center">
            <Grid item xs={12} md={6} sx={{ order: { xs: 2, md: 1 } }}>
              <Box
                sx={{
                  p: { xs: 3, md: 4 },
                  borderRadius: "20px",
                  background: "linear-gradient(135deg, rgba(118,75,162,0.06) 0%, rgba(91,124,250,0.06) 100%)",
                  border: "1px solid rgba(118,75,162,0.12)",
                  display: "flex",
                  flexDirection: "column",
                  gap: 2.5,
                }}
              >
                {[
                  { icon: "📱", label: "Cross-Platform Compatibility" },
                  { icon: "🎨", label: "Intuitive UX/UI Design" },
                  { icon: "🚀", label: "High-Performance Delivery" },
                ].map((item) => (
                  <Box key={item.label} sx={{ display: "flex", alignItems: "center", gap: 2.5 }}>
                    <Box
                      sx={{
                        width: 44,
                        height: 44,
                        borderRadius: "12px",
                        background: "linear-gradient(135deg, rgba(118,75,162,0.12), rgba(91,124,250,0.12))",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: "1.3rem",
                        flexShrink: 0,
                      }}
                    >
                      {item.icon}
                    </Box>
                    <Typography sx={{ fontWeight: 600, color: "#1b1f33", fontSize: { xs: "16px", sm: "18px" } }}>
                      {item.label}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Grid>
            <Grid item xs={12} md={6} sx={{ order: { xs: 1, md: 2 } }}>
              <Typography
                variant="h3"
                sx={{
                  fontWeight: 700,
                  fontSize: { xs: "28px", sm: "36px", md: "42px" },
                  color: "#1b1f33",
                  mb: 3,
                }}
              >
                Innovating{" "}
                <Box
                  component="span"
                  sx={{
                    background: "linear-gradient(135deg, #5b7cfa 0%, #764ba2 100%)",
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                  }}
                >
                  Seamless Application Experiences
                </Box>
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  color: "#4c5472",
                  lineHeight: 1.8,
                  fontSize: { xs: "16px", sm: "19px", md: "20px" },
                  mb: 3,
                  textAlign: "justify",
                }}
              >
                At Briozon, we specialize in crafting seamless, high-performance applications that deliver exceptional user experiences across all devices. Our focus on innovation ensures that every solution is intuitive and responsive. We empower businesses to stay ahead with cutting-edge technologies tailored to their needs.
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  color: "#4c5472",
                  lineHeight: 1.8,
                  fontSize: { xs: "16px", sm: "19px", md: "20px" },
                  textAlign: "justify",
                }}
              >
                We create applications that not only meet functional requirements but also prioritize smooth, intuitive user experiences. By integrating the latest technologies and focusing on design, we ensure optimal performance on all platforms. Briozon delivers solutions that adapt to the evolving needs of modern businesses.
              </Typography>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Section 4: Application Expertise Cards */}
      <Box
        sx={{
          py: { xs: 8, md: 10 },
          background: "linear-gradient(135deg, #f0f4ff 0%, #f7f0ff 100%)",
        }}
      >
        <Container maxWidth="lg">
          <Box sx={{ textAlign: "center", mb: { xs: 5, md: 6 } }}>
            <Typography
              variant="h3"
              sx={{
                fontWeight: 700,
                fontSize: { xs: "28px", sm: "36px", md: "42px" },
                color: "#1b1f33",
                mb: 2,
              }}
            >
              Our{" "}
              <Box
                component="span"
                sx={{
                  background: "linear-gradient(135deg, #5b7cfa 0%, #764ba2 100%)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                }}
              >
                Application Expertise
              </Box>
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color: "#4c5472",
                lineHeight: 1.8,
                maxWidth: 700,
                mx: "auto",
                fontSize: { xs: "16px", sm: "19px", md: "20px" },
              }}
            >
              Briozon boasts extensive expertise in crafting tailored application solutions across a variety of industries and use cases.
            </Typography>
          </Box>

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)", md: "repeat(3, 1fr)" },
              gap: { xs: 3, sm: 4 },
            }}
          >
            {[
              { icon: "🛒", title: "E-Commerce Solutions", desc: "E-Commerce and content management portal development with seamless user experiences." },
              { icon: "🏢", title: "Portal Development", desc: "Portal solutions for customers, suppliers, and partners with customized workflows." },
              { icon: "💻", title: "Enterprise UI Development", desc: "UI and interface development for SAP, Oracle apps, and Java-based applications." },
              { icon: "⚛️", title: "Modern Framework Development", desc: "Custom development using React, Java, and .Net with cutting-edge technologies." },
              { icon: "🔄", title: "Legacy Migration", desc: "Migration of PHP, .NET, and Java-based applications to React/Angular." },
            ].map((item, index) => (
              <Box
                key={index}
                sx={{
                  p: { xs: 3, sm: 4 },
                  background: "#fff",
                  borderRadius: "16px",
                  border: "1px solid #e8eaf0",
                  borderLeft: "4px solid transparent",
                  boxShadow: "0 2px 12px rgba(0,0,0,0.06)",
                  transition: "all 0.3s ease",
                  animation: `${fadeInUp} 0.6s ease-out ${index * 0.08}s both`,
                  "&:hover": {
                    transform: "translateY(-6px)",
                    boxShadow: "0 16px 40px rgba(91, 124, 250, 0.14)",
                    borderLeft: "4px solid #5b7cfa",
                    borderColor: "#e8eaf0",
                    "& .app-icon": {
                      transform: "scale(1.12)",
                      background: "linear-gradient(135deg, rgba(91,124,250,0.25), rgba(118,75,162,0.25))",
                    },
                    "& .app-title": { color: "#5b7cfa" },
                  },
                }}
              >
                <Box
                  className="app-icon"
                  sx={{
                    width: { xs: "52px", sm: "58px" },
                    height: { xs: "52px", sm: "58px" },
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    mb: 2,
                    borderRadius: "14px",
                    background: "linear-gradient(135deg, rgba(91,124,250,0.12), rgba(118,75,162,0.12))",
                    fontSize: { xs: "26px", sm: "30px" },
                    transition: "all 0.3s ease",
                  }}
                >
                  {item.icon}
                </Box>
                <Typography
                  className="app-title"
                  variant="h6"
                  sx={{
                    fontWeight: 700,
                    color: "#1b1f33",
                    mb: 1.5,
                    fontSize: { xs: "20px", sm: "22px" },
                    transition: "color 0.3s ease",
                  }}
                >
                  {item.title}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    color: "#4c5472",
                    lineHeight: 1.7,
                    fontSize: { xs: "16px", sm: "17px" },
                  }}
                >
                  {item.desc}
                </Typography>
              </Box>
            ))}
          </Box>
        </Container>
      </Box>

      {/* Section 5: Why Choose Us */}
      <Box sx={{ py: { xs: 8, md: 10 }, backgroundColor: "#fff" }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: "center", mb: { xs: 5, md: 6 } }}>
            <Typography
              variant="h3"
              sx={{
                fontWeight: 700,
                fontSize: { xs: "28px", sm: "36px", md: "42px" },
                color: "#1b1f33",
                mb: 1,
              }}
            >
              Your Trusted Partner in{" "}
              <Box
                component="span"
                sx={{
                  background: "linear-gradient(135deg, #5b7cfa 0%, #764ba2 100%)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                }}
              >
                Achieving Success
              </Box>
            </Typography>
          </Box>

          <Grid container spacing={3}>
            {whyChooseUs.map((item, index) => (
              <Grid item xs={12} sm={6} key={index}>
                <Box
                  sx={{
                    p: { xs: 3, sm: 4 },
                    display: "flex",
                    alignItems: "flex-start",
                    gap: 3,
                    borderRadius: "16px",
                    backgroundColor: "#fafbff",
                    border: "1px solid #e8eaf0",
                    boxShadow: "0 2px 12px rgba(0,0,0,0.06)",
                    transition: "all 0.3s ease",
                    animation: `${fadeInUp} 0.6s ease-out ${index * 0.08}s both`,
                    "&:hover": {
                      transform: "translateY(-4px)",
                      boxShadow: "0 12px 32px rgba(91, 124, 250, 0.12)",
                      borderColor: "rgba(91, 124, 250, 0.3)",
                      backgroundColor: "#fff",
                    },
                  }}
                >
                  <Box
                    sx={{
                      width: { xs: "52px", sm: "58px" },
                      height: { xs: "52px", sm: "58px" },
                      borderRadius: "14px",
                      background: "linear-gradient(135deg, rgba(91,124,250,0.12), rgba(118,75,162,0.12))",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: { xs: "26px", sm: "30px" },
                      flexShrink: 0,
                    }}
                  >
                    {item.icon}
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 700,
                        color: "#1b1f33",
                        mb: 1,
                        fontSize: { xs: "20px", sm: "22px" },
                      }}
                    >
                      {item.title}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        color: "#4c5472",
                        lineHeight: 1.7,
                        fontSize: { xs: "16px", sm: "17px" },
                      }}
                    >
                      {item.desc}
                    </Typography>
                  </Box>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Section 6: Service Offerings */}
      <Box
        sx={{
          py: { xs: 8, md: 10 },
          background: "linear-gradient(135deg, #f0f4ff 0%, #f7f0ff 100%)",
        }}
      >
        <Container maxWidth="lg">
          <Box sx={{ textAlign: "center", mb: { xs: 5, md: 6 } }}>
            <Typography
              variant="h3"
              sx={{
                fontWeight: 700,
                fontSize: { xs: "28px", sm: "36px", md: "42px" },
                color: "#1b1f33",
                mb: 2,
              }}
            >
              Our Service{" "}
              <Box
                component="span"
                sx={{
                  background: "linear-gradient(135deg, #5b7cfa 0%, #764ba2 100%)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                }}
              >
                Offerings
              </Box>
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color: "#4c5472",
                lineHeight: 1.8,
                maxWidth: 700,
                mx: "auto",
                fontSize: { xs: "16px", sm: "19px", md: "20px" },
              }}
            >
              Fostering Exceptional Customer Relationships through comprehensive service offerings.
            </Typography>
          </Box>

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)", md: "repeat(3, 1fr)" },
              gap: { xs: 3, sm: 4 },
            }}
          >
            {serviceOfferings.map((item, index) => (
              <Box
                key={index}
                sx={{
                  p: { xs: 3, sm: 4 },
                  background: "#fff",
                  borderRadius: "16px",
                  border: "1px solid #e8eaf0",
                  borderLeft: "4px solid transparent",
                  boxShadow: "0 2px 12px rgba(0,0,0,0.06)",
                  transition: "all 0.3s ease",
                  animation: `${fadeInUp} 0.6s ease-out ${index * 0.08}s both`,
                  "&:hover": {
                    transform: "translateY(-6px)",
                    boxShadow: "0 16px 40px rgba(91, 124, 250, 0.14)",
                    borderLeft: "4px solid #5b7cfa",
                    borderColor: "#e8eaf0",
                    "& .offering-icon": {
                      transform: "scale(1.12)",
                      background: "linear-gradient(135deg, rgba(91,124,250,0.25), rgba(118,75,162,0.25))",
                    },
                    "& .offering-title": { color: "#5b7cfa" },
                  },
                }}
              >
                <Box
                  className="offering-icon"
                  sx={{
                    width: { xs: "52px", sm: "58px" },
                    height: { xs: "52px", sm: "58px" },
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    mb: 2,
                    borderRadius: "14px",
                    background: "linear-gradient(135deg, rgba(91,124,250,0.12), rgba(118,75,162,0.12))",
                    fontSize: { xs: "26px", sm: "30px" },
                    transition: "all 0.3s ease",
                  }}
                >
                  {item.icon}
                </Box>
                <Typography
                  className="offering-title"
                  variant="h6"
                  sx={{
                    fontWeight: 700,
                    color: "#1b1f33",
                    mb: 1.5,
                    fontSize: { xs: "20px", sm: "22px" },
                    transition: "color 0.3s ease",
                  }}
                >
                  {item.title}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    color: "#4c5472",
                    lineHeight: 1.7,
                    fontSize: { xs: "16px", sm: "17px" },
                  }}
                >
                  {item.desc}
                </Typography>
              </Box>
            ))}
          </Box>
        </Container>
      </Box>

      {/* Contact Section */}
      <Box
        sx={{
          py: { xs: 8, sm: 10, md: 12 },
          background: "linear-gradient(135deg, #5b7cfa 0%, #764ba2 100%)",
          position: "relative",
          overflow: "hidden",
        }}
      >
        {/* Decorative circles */}
        <Box
          sx={{
            position: "absolute",
            top: "-50px",
            right: "-50px",
            width: 200,
            height: 200,
            borderRadius: "50%",
            background: "rgba(255,255,255,0.1)",
          }}
        />
        <Box
          sx={{
            position: "absolute",
            bottom: "-30px",
            left: "-30px",
            width: 150,
            height: 150,
            borderRadius: "50%",
            background: "rgba(255,255,255,0.08)",
          }}
        />

        <Container maxWidth="md">
          <Box sx={{ textAlign: "center", position: "relative", zIndex: 1 }}>
            <Typography
              variant="h3"
              sx={{
                fontSize: { xs: "28px", sm: "36px", md: "42px" },
                fontWeight: 800,
                color: "#fff",
                mb: 2,
              }}
            >
              Custom Application Development for Transformation
            </Typography>
            <Typography
              variant="h6"
              sx={{
                fontSize: { xs: "16px", sm: "19px", md: "20px" },
                color: "rgba(255,255,255,0.9)",
                mb: 4,
                maxWidth: "700px",
                mx: "auto",
                lineHeight: 1.8,
              }}
            >
              At the intersection of business needs and technology, we specialize in custom application development, addressing complex challenges with innovative, tailored solutions. Our team of experts works closely with you to craft applications that streamline operations, enhance efficiency, and enable your business to adapt to evolving demands.
            </Typography>
            <Button
              component="a"
              href="/contact"
              variant="contained"
              sx={{
                backgroundColor: "#fff",
                color: "#5b7cfa",
                fontWeight: 700,
                px: 5,
                py: 1.5,
                borderRadius: "12px",
                fontSize: "16px",
                textTransform: "none",
                transition: "all 0.3s ease",
                boxShadow: "0 8px 24px rgba(91, 124, 250, 0.08)",
                "&:hover": {
                  backgroundColor: "#f0f0f0",
                  transform: "translateY(-4px)",
                  boxShadow: "0 12px 24px rgba(0,0,0,0.12)",
                },
              }}
            >
              Let's Connect
            </Button>
          </Box>
        </Container>
      </Box>
    </Box>
  );
};

export default CustomAppDevelopment;
